"use client"

import type React from "react"
import { useState, useRef, useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { RotateCcw } from "lucide-react"

export type AspectRatio = "3:2" | "1:1"

interface ImageCropperProps {
  imageSrc: string
  onCroppedImage: (croppedImage: string) => void
  onImageSaved: (savedImageUrl: string, fileName: string, fileSize: number) => void
}

export function ImageCropper({ imageSrc, onCroppedImage, onImageSaved }: ImageCropperProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const saveCanvasRef = useRef<HTMLCanvasElement>(null)

  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("3:2")
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 })
  const [imageScale, setImageScale] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 })
  const [currentCroppedImage, setCurrentCroppedImage] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const hasDraggedRef = useRef(false)
  const hasInitializedRef = useRef(false)

  // Target resolutions for different aspect ratios
  const getTargetResolution = (aspectRatio: AspectRatio) => {
    switch (aspectRatio) {
      case "3:2":
        return { width: 1536, height: 1024 }
      case "1:1":
        return { width: 1024, height: 1024 }
      default:
        return { width: 1536, height: 1024 }
    }
  }

  const { width: TARGET_WIDTH, height: TARGET_HEIGHT } = getTargetResolution(aspectRatio)

  // Calculate crop area dimensions based on selected aspect ratio
  const getCropDimensions = useCallback(() => {
    if (!containerSize.width || !containerSize.height) return { width: 0, height: 0 }

    let cropWidth: number
    let cropHeight: number

    if (aspectRatio === "1:1") {
      // Square crop - use the smaller dimension with some padding
      const maxSize = Math.min(containerSize.width, containerSize.height) - 20
      cropWidth = maxSize
      cropHeight = maxSize
    } else {
      // 3:2 crop - use nearly full container width
      cropWidth = containerSize.width - 20
      cropHeight = cropWidth / (3 / 2) // 3:2 aspect ratio

      // Make sure crop height doesn't exceed container height (with some padding)
      const maxCropHeight = containerSize.height - 40
      if (cropHeight > maxCropHeight) {
        const adjustedCropHeight = maxCropHeight
        const adjustedCropWidth = adjustedCropHeight * (3 / 2)
        cropWidth = adjustedCropWidth
        cropHeight = adjustedCropHeight
      }
    }

    return { width: cropWidth, height: cropHeight }
  }, [containerSize, aspectRatio])

  // Update container size on mount and resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setContainerSize({ width: rect.width, height: rect.height })
      }
    }

    updateSize()
    window.addEventListener("resize", updateSize)
    return () => window.removeEventListener("resize", updateSize)
  }, [])

  // Initialize image positioning when image loads
  useEffect(() => {
    if (imageRef.current && containerSize.width && containerSize.height) {
      const img = imageRef.current

      const onLoad = () => {
        setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight })

        if (containerSize.width && containerSize.height && !hasInitializedRef.current) {
          // Scale image to fill the full container width
          const initialScale = containerSize.width / img.naturalWidth
          setImageScale(initialScale)
          setImagePosition({ x: 0, y: 0 })
          hasInitializedRef.current = true
        }
      }

      if (img.complete) {
        onLoad()
      } else {
        img.addEventListener("load", onLoad)
        return () => img.removeEventListener("load", onLoad)
      }
    }
  }, [imageSrc, containerSize])

  useEffect(() => {
    hasInitializedRef.current = false
  }, [imageSrc])

  // Generate preview cropped image (for display purposes)
  const generatePreviewCroppedImage = useCallback(() => {
    if (!canvasRef.current || !imageRef.current || !imageNaturalSize.width) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const cropDims = getCropDimensions()
    if (!cropDims.width || !cropDims.height) return

    // Set canvas size to crop dimensions for preview
    canvas.width = cropDims.width
    canvas.height = cropDims.height

    // Calculate the actual displayed image dimensions
    const displayedImageWidth = imageNaturalSize.width * imageScale
    const displayedImageHeight = imageNaturalSize.height * imageScale

    // Calculate crop area position relative to the image
    const cropX = (containerSize.width - cropDims.width) / 2 - imagePosition.x
    const cropY = (containerSize.height - cropDims.height) / 2 - imagePosition.y

    // Convert to source image coordinates
    const scaleRatio = imageNaturalSize.width / displayedImageWidth
    const sourceX = cropX * scaleRatio
    const sourceY = cropY * scaleRatio
    const sourceWidth = cropDims.width * scaleRatio
    const sourceHeight = cropDims.height * scaleRatio

    // Draw the cropped portion
    ctx.drawImage(imageRef.current, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, cropDims.width, cropDims.height)

    const croppedDataUrl = canvas.toDataURL("image/png")
    setCurrentCroppedImage(croppedDataUrl)
    return croppedDataUrl
  }, [imagePosition, imageScale, imageNaturalSize, containerSize, getCropDimensions])

  // Generate high-resolution cropped image for saving
  const generateHighResCroppedImage = useCallback(() => {
    if (!saveCanvasRef.current || !imageRef.current || !imageNaturalSize.width) return null

    const canvas = saveCanvasRef.current
    const ctx = canvas.getContext("2d")
    if (!ctx) return null

    const cropDims = getCropDimensions()
    if (!cropDims.width || !cropDims.height) return null

    // Set canvas size to target resolution
    canvas.width = TARGET_WIDTH
    canvas.height = TARGET_HEIGHT

    // Calculate the actual displayed image dimensions
    const displayedImageWidth = imageNaturalSize.width * imageScale
    const displayedImageHeight = imageNaturalSize.height * imageScale

    // Calculate crop area position relative to the image
    const cropX = (containerSize.width - cropDims.width) / 2 - imagePosition.x
    const cropY = (containerSize.height - cropDims.height) / 2 - imagePosition.y

    // Convert to source image coordinates
    const scaleRatio = imageNaturalSize.width / displayedImageWidth
    const sourceX = cropX * scaleRatio
    const sourceY = cropY * scaleRatio
    const sourceWidth = cropDims.width * scaleRatio
    const sourceHeight = cropDims.height * scaleRatio

    // Draw the cropped portion at high resolution
    ctx.drawImage(imageRef.current, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, TARGET_WIDTH, TARGET_HEIGHT)

    const highResCroppedDataUrl = canvas.toDataURL("image/png")
    return highResCroppedDataUrl
  }, [imagePosition, imageScale, imageNaturalSize, containerSize, getCropDimensions, TARGET_WIDTH, TARGET_HEIGHT])

  // Generate preview cropped image whenever position or scale changes
  useEffect(() => {
    generatePreviewCroppedImage()
  }, [imagePosition, imageScale, imageNaturalSize, containerSize, aspectRatio])

  // Simulate save process with high-resolution output and directory structure
  const saveCroppedImage = useCallback(async () => {
    if (!currentCroppedImage) return

    setIsSaving(true)

    try {
      // Generate high-resolution version for saving
      const highResCroppedImage = generateHighResCroppedImage()

      if (!highResCroppedImage) {
        throw new Error("Failed to generate high-resolution image")
      }

      // Generate unique filename with aspect ratio indicator
      const timestamp = Date.now()
      const aspectRatioSuffix = aspectRatio === "1:1" ? "square" : "3x2"
      const savedFileName = `room-cropped-${aspectRatioSuffix}-${timestamp}.png`
      const savedFilePath = `/saved-images/${savedFileName}`

      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // For now, simulate successful save
      const fileSize = Math.round(highResCroppedImage.length * 0.75) // Approximate file size

      // Call the parent callbacks with the high-resolution image
      onCroppedImage(highResCroppedImage)
      onImageSaved(highResCroppedImage, savedFileName, fileSize)
    } catch (error) {
      console.error("Error in save simulation:", error)
      alert("An error occurred while saving the image. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }, [
    currentCroppedImage,
    generateHighResCroppedImage,
    onCroppedImage,
    onImageSaved,
    aspectRatio,
    TARGET_WIDTH,
    TARGET_HEIGHT,
  ])

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return

    const newX = e.clientX - dragStart.x
    const newY = e.clientY - dragStart.y

    setImagePosition({ x: newX, y: newY })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return

    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    }
    hasDraggedRef.current = false

    setIsDragging(true)
    setDragStart({ x: e.touches[0].clientX - imagePosition.x, y: e.touches[0].clientY - imagePosition.y })
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return

    if (touchStartRef.current && !hasDraggedRef.current) {
      const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x)
      const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y)

      // If vertical movement is greater than horizontal, allow scrolling
      if (deltaY > deltaX && deltaY > 10) {
        setIsDragging(false)
        touchStartRef.current = null
        return
      }

      // If horizontal movement exceeds threshold, start preventing default
      if (deltaX > 10 || deltaY > 10) {
        hasDraggedRef.current = true
      }
    }

    // Only prevent default and update position if we've determined this is a drag gesture
    if (hasDraggedRef.current) {
      e.preventDefault()
      const newX = e.touches[0].clientX - dragStart.x
      const newY = e.touches[0].clientY - dragStart.y
      setImagePosition({ x: newX, y: newY })
    }
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
    touchStartRef.current = null
    hasDraggedRef.current = false
  }

  const handleReset = () => {
    setImagePosition({ x: 0, y: 0 })
    if (imageRef.current && imageNaturalSize.width && containerSize.width) {
      const initialScale = containerSize.width / imageNaturalSize.width
      setImageScale(initialScale)
      hasInitializedRef.current = true
    }
  }

  const cropDims = getCropDimensions()

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div></div>

        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">Crop Your Photo</h3>
          <p className="text-sm text-gray-600">Choose format, adjust position and zoom, then save</p>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={handleReset}
            className="flex items-center space-x-2 bg-transparent"
            disabled={isSaving}
          >
            <RotateCcw className="h-4 w-4" />
            <span>Reset</span>
          </Button>
        </div>
      </div>

      {/* Crop Area */}
      <div
        ref={containerRef}
        className="relative bg-gray-100 rounded-lg overflow-hidden"
        style={{ height: "500px" }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Image */}
        <img
          ref={imageRef}
          src={imageSrc || "/placeholder.svg"}
          alt="Room to crop"
          className="absolute cursor-move select-none"
          style={{
            width: `${imageNaturalSize.width * imageScale}px`,
            height: `${imageNaturalSize.height * imageScale}px`,
            transform: `translate(${imagePosition.x}px, ${imagePosition.y}px)`,
            left: 0,
            top: 0,
            touchAction: "none",
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          draggable={false}
        />

        {/* Crop Overlay */}
        <div
          className="absolute border-2 border-white shadow-lg pointer-events-none"
          style={{
            width: `${cropDims.width}px`,
            height: `${cropDims.height}px`,
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
          }}
        >
          {/* Corner indicators */}
          <div className="absolute -top-1 -left-1 w-3 h-3 bg-white border border-gray-300"></div>
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-white border border-gray-300"></div>
          <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-white border border-gray-300"></div>
          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border border-gray-300"></div>
        </div>

        {/* Instructions */}
        <div className="absolute bottom-4 left-4 bg-black bg-opacity-75 text-white px-3 py-2 rounded text-sm">
          Drag image to reposition • Use zoom controls below
        </div>

        {/* Save button overlay */}
        <div className="absolute top-4 right-4">
          <Button
            onClick={saveCroppedImage}
            disabled={!currentCroppedImage || isSaving}
            className="bg-[#40A1AB] hover:bg-[#3A919A] text-white px-6 py-2"
          >
            {isSaving ? "Saving..." : "Save Crop"}
          </Button>
        </div>
      </div>

      {/* Format Selection & Controls */}
      <div className="space-y-6">
        {/* Format Selection */}
        <div className="text-center">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Choose Crop Format</h4>
          <p className="text-sm text-gray-600 italic mb-3">Landscape images require more credits than square images</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {/* 3:2 Option */}
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                aspectRatio === "3:2"
                  ? "border-[#40A1AB] bg-teal-50 ring-2 ring-teal-200"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => setAspectRatio("3:2")}
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-8 bg-gray-300 rounded flex-shrink-0"></div>
                <div className="text-left">
                  <h5 className="font-semibold text-gray-900">Landscape (3:2)</h5>
                  <p className="text-sm text-gray-600">1536 × 1024 pixels</p>
                  <p className="text-xs text-gray-500">Perfect for wide room shots</p>
                </div>
                {aspectRatio === "3:2" && (
                  <div className="ml-auto">
                    <div className="w-5 h-5 bg-[#40A1AB] rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 1:1 Option */}
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                aspectRatio === "1:1"
                  ? "border-[#40A1AB] bg-teal-50 ring-2 ring-teal-200"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => setAspectRatio("1:1")}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-300 rounded flex-shrink-0"></div>
                <div className="text-left">
                  <h5 className="font-semibold text-gray-900">Square (1:1)</h5>
                  <p className="text-sm text-gray-600">1024 × 1024 pixels</p>
                  <p className="text-xs text-gray-500">Great for social media</p>
                </div>
                {aspectRatio === "1:1" && (
                  <div className="ml-auto">
                    <div className="w-5 h-5 bg-[#40A1AB] rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Zoom Controls */}
        <div className="flex justify-center">
          <div className="flex items-center space-x-4 bg-white border border-gray-200 rounded-lg px-6 py-3">
            <label className="text-sm font-medium text-gray-700">Zoom:</label>

            {/* Decrement button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const baseScale =
                  containerSize.width && imageNaturalSize.width ? containerSize.width / imageNaturalSize.width : 1
                const newScale = Math.max(baseScale * 0.5, imageScale - baseScale * 0.05)
                setImageScale(newScale)
              }}
              className="w-8 h-8 p-0 flex items-center justify-center"
              disabled={isSaving}
            >
              -
            </Button>

            {/* Zoom slider */}
            <input
              type="range"
              min={
                containerSize.width && imageNaturalSize.width
                  ? (containerSize.width / imageNaturalSize.width) * 0.5 // 50% of full width
                  : 0.5
              }
              max={
                containerSize.width && imageNaturalSize.width
                  ? (containerSize.width / imageNaturalSize.width) * 1.5 // 150% of full width
                  : 1.5
              }
              step={
                containerSize.width && imageNaturalSize.width
                  ? (containerSize.width / imageNaturalSize.width) * 0.05 // 5% increments
                  : 0.05
              }
              value={imageScale}
              onChange={(e) => setImageScale(Number.parseFloat(e.target.value))}
              className="w-32"
              disabled={isSaving}
            />

            {/* Increment button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const baseScale =
                  containerSize.width && imageNaturalSize.width ? containerSize.width / imageNaturalSize.width : 1
                const newScale = Math.min(baseScale * 1.5, imageScale + baseScale * 0.05)
                setImageScale(newScale)
              }}
              className="w-8 h-8 p-0 flex items-center justify-center"
              disabled={isSaving}
            >
              +
            </Button>

            {/* Percentage display */}
            <span className="text-xs text-gray-500 min-w-[3rem]">
              {containerSize.width && imageNaturalSize.width
                ? Math.round((imageScale / (containerSize.width / imageNaturalSize.width)) * 100)
                : Math.round(imageScale * 100)}
              %
            </span>
          </div>
        </div>

        {/* Current Selection Info */}
        <div className="text-center">
          <div className="inline-flex items-center space-x-2 bg-gray-50 px-4 py-2 rounded-lg">
            <span className="text-sm text-gray-600">Output:</span>
            <span className="text-sm font-medium text-gray-900">
              {TARGET_WIDTH}×{TARGET_HEIGHT} ({aspectRatio === "1:1" ? "Square" : "Landscape"})
            </span>
          </div>
        </div>
      </div>

      {/* Hidden canvases for generating cropped images */}
      <canvas ref={canvasRef} className="hidden" />
      <canvas ref={saveCanvasRef} className="hidden" />
    </div>
  )
}
